//Write a component that is a column that lists each project
// we will fetch all of the projects for a given user then display them
// They will be displayed such that each one is clickable and will expand, withink this expanded one can select To do, Complete

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import AddBoxIcon from "../../../../public/assets/icons/add-box.svg";
import MinusBoxIcon from "../../../../public/assets/icons/minus-box.svg";
import { QueryGroupUserRoles } from "../../../queries/GroupUserRoles";
import { QueryWorkers } from "../../../queries/Workers";
import { AudienceConfig } from "../../../types/cqa";
import { fetchFullRubricVersion, getQaRules } from "../../../utils/api";
import { omitKey, policiesContext, serviceLineMapper } from "../../../utils/common";
import filterProjects from "../../../utils/projectFilterUtils";
import ConfigurableList, { ListSection } from "../../atoms/ConfigurableList";
import { ListItem } from "../../atoms/ConfigurableList/ListItem";
import { LABEL_INDIVIDUAL } from "../../organisms/QARuleForm/constants";
import { NAVIGATION_COLORS, STORAGE_KEYS } from "../NavigationColumn/constants";
import { multiUserProjectDefault, multiUserProjectFiltersCoder, singleUserProjectFiltersCoder } from "./constants";
import { fetchProjectsWithCounts } from "./utilsProjectSelect";
import { CollectionWithWorkItemCounts, WorkitemStatus } from "./types";

interface QANavigationContentProps {
    searchTerm: string;
    client: any;
    onProjectSelected: (project: any, status?: WorkitemStatus | string | null) => void;
    selectedProject: any;
    selectedStatus: WorkitemStatus | null;
    initialProjectId?: string | null;
    initialStatus?: WorkitemStatus | null;
    onCountIsLoadingChange?: (isLoading: boolean) => void;
    onProjectUpdated?: (updatedProject: any) => void;
    setLoadingProjectList?: (isLoading: boolean) => void;
}

// event name for work items updates
const WORK_ITEMS_UPDATED_EVENT = 'workitems-updated';

// event name for QA rule updates (exported for use in QARuleForm)
export const QA_RULE_UPDATED_EVENT = 'qa-rule-updated';

export const createProjectItemFromQaRule = (qaRule: any, activeContextRoles: any[], workerId: string) => {
    const collectionIdFilter = {
        "key": "qa_collection_id",
        "operation": "eq",
        "value": qaRule.data.collection_id
    }

    const multiUserfilter = activeContextRoles.includes("cqa_manager") || qaRule.data.project_configuration.auditors?.includes(workerId) ? multiUserProjectDefault : multiUserProjectFiltersCoder;
    const audience: AudienceConfig = qaRule.data.project_configuration.audience;
    const audienceConfigured = audience?.distributionMethod;
    const individualDistribution = audienceConfigured && audience.distributionMethod == LABEL_INDIVIDUAL;
    // represents legacy approach to determining whether workitems show up by group association or multiuser configuration (individual assignees)
    const filters = qaRule.data.assignee_ids == null
            ? [...singleUserProjectFiltersCoder, collectionIdFilter]
            : [...multiUserfilter, collectionIdFilter];
    // represents next version of workitem distribuiton, based on audience configuration
    const audienceBasedFilters = individualDistribution ? [...multiUserfilter, collectionIdFilter] : [...singleUserProjectFiltersCoder, collectionIdFilter];

    return {
        id: qaRule.id,
        name: qaRule.data.name,
        collection_id: qaRule.data.collection_id,
        availability: qaRule.data.availability,
        filters: audienceConfigured ? audienceBasedFilters : filters,
        assignee_ids: qaRule.data.assignee_ids,
        rubricVersionId: qaRule.data.project_configuration?.rubricVersionId,
        project_configuration: qaRule.data.project_configuration,
    }
}

export function getUserRolesFromActiveContext(data) {
    if (!data || !data.activeContext || !data.user || !data.user.roles) {
        return [];
    }

    const tenantId = data.activeContext.tenantId;

    // Find the matching roles object in user.roles array
    const matchingRole = data.user.roles.find(role => role.tenantId === tenantId);

    // Extract role names into an array
    return matchingRole ? matchingRole.roles.map(r => r.roleName) : [];
}

export function getAvailableStatuses(userContext){
    const activeContextRoles = getUserRolesFromActiveContext(userContext);

    const isCQAManager = activeContextRoles.includes("cqa_manager");
    
    // Get available statuses based on user role
    return isCQAManager
        ? [WorkitemStatus.ASSIGNED_TO_ME, WorkitemStatus.IN_CQA, WorkitemStatus.CQA_COMPLETE, WorkitemStatus.CQA_APPROVED, WorkitemStatus.CQA_AUDIT, WorkitemStatus.CQA_AUDIT_COMPLETE]
        : [WorkitemStatus.IN_CQA, WorkitemStatus.CQA_COMPLETE];
}

const REFRESH_INTERVAL_MS = 30000; // 30 seconds

const QANavigationContent: React.FC<QANavigationContentProps> = ({
    searchTerm,
    client,
    onProjectSelected,
    selectedProject,
    selectedStatus,
    initialProjectId,
    initialStatus,
    onCountIsLoadingChange,
    onProjectUpdated,
    setLoadingProjectList = () => {},
}) => {
    const { userContext, roles } = useContext(policiesContext);
    const theme = useTheme();

    const [projectList, setProjectList] = useState<{
        active: CollectionWithWorkItemCounts[],
        forReview: CollectionWithWorkItemCounts[],
        archived: CollectionWithWorkItemCounts[]
    }>({ active: [], forReview: [], archived: [] });
    const [userRoles, setUserRoles] = useState([]);
    const [workerId, setWorkerId] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isActiveExpanded, setIsActiveExpanded] = useState(true); // Default: Active Projects expanded
    const [isArchivedExpanded, setIsArchivedExpanded] = useState(false);
    const [isForReviewExpanded, setIsForReviewExpanded] = useState(false);
    const activeContextRoles = getUserRolesFromActiveContext(userContext);
    const showCounts = activeContextRoles.includes("cqa_manager") || activeContextRoles.includes("cqa_coder");
    const [countIsLoading, setCountIsLoading] = useState(true);
    const [rubricInfoCache, setRubricInfoCache] = useState<Map<number, { name: string; version: string }>>(new Map());
    const rubricInfoCacheRef = useRef<Map<number, { name: string; version: string }>>(new Map());



    // Restore expanded section from session storage on mount
    useEffect(() => {
        const savedSection = sessionStorage.getItem(STORAGE_KEYS.QA_EXPANDED_SECTION);
        if (savedSection === 'active') {
            setIsActiveExpanded(true);
            setIsForReviewExpanded(false);
            setIsArchivedExpanded(false);
        } else if (savedSection === 'forReview') {
            setIsActiveExpanded(false);
            setIsForReviewExpanded(true);
            setIsArchivedExpanded(false);
        } else if (savedSection === 'archived') {
            setIsActiveExpanded(false);
            setIsForReviewExpanded(false);
            setIsArchivedExpanded(true);
        }
        // If no saved section, default to Active Projects expanded (isActiveExpanded is already true by default)
    }, []);
    
    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            // Fetch user roles
            client.mutate({
                mutation: QueryGroupUserRoles,
                variables: {
                    groupUserRolesQuery: {
                        query: `Select * from group_user_roles where user_id='${userContext.user.userId}' and status='ACTIVE';`
                    }
                }
            }),
            // Fetch worker info
            client.mutate({
                mutation: QueryWorkers,
                variables: {
                    workerQuery: {
                        query: `Select * from worker where user_id='${userContext.user.userId}' and subtenant_id = '${userContext.activeContext.tenantId}' and status='ACTIVE';`
                    }
                }
            })

            
        ]).then(([rolesResponse, workerResponse]) => {
            setUserRoles(rolesResponse.data.QueryGroupUserRoles);
            if (workerResponse.data.QueryWorker.length) {
                setWorkerId(workerResponse.data.QueryWorker[0].worker_id);
            } else {
                // user does not have a worker record so project list isn't expected to be called
                setLoadingProjectList(false);
            }
            setIsLoading(false);
        }).catch((error) => {
            console.error("Error fetching user data:", error);
            setIsLoading(false);
        });
    }, [userContext, client]);

    // Second useEffect that depends on userRoles and workerId
    useEffect(() => {
        if (!isLoading && workerId) {
            getProjectList();
        }
    }, [isLoading, workerId]);

    // Function to get all refreshable projects including archived with availability !== false
    const getRefreshableProjects = useCallback(() => {
        return [
            ...projectList.active,
            ...projectList.forReview,
            ...projectList.archived.filter(project => project.availability !== false)
        ];
    }, [projectList]);

    // Memoized count of refreshable projects - only changes when projects are added/removed/moved also useEffect only re-runs when list changes, not when counts update
    const refreshableProjectsCount = useMemo(() => {
        return projectList.active.length + 
               projectList.forReview.length + 
               projectList.archived.filter(p => p.availability !== false).length;
    }, [projectList.active.length, projectList.forReview.length, projectList.archived.length]);

    // Function to refresh project counts
    const refreshProjectCounts = useCallback(() => {
        const refreshableProjects = getRefreshableProjects();
        if (refreshableProjects.length > 0) {
            getProjectWorkItemCounts(refreshableProjects, false);
        }
    }, [getRefreshableProjects]);

    // Listen for work items update events dispatched from WorkItemTable after revert/approve
    useEffect(() => {
        const handleWorkItemsUpdated = () => {
            // Small delay to sync with backend 
            setTimeout(() => {
                refreshProjectCounts();
            }, 500);
        };

        window.addEventListener(WORK_ITEMS_UPDATED_EVENT, handleWorkItemsUpdated);

        return () => {
            window.removeEventListener(WORK_ITEMS_UPDATED_EVENT, handleWorkItemsUpdated);
        };
    }, [refreshProjectCounts]);

    // Listen for QA rule update events dispatched from QARuleForm after update
    useEffect(() => {
        const handleQaRuleUpdated = () => {
            // Refresh the entire project list to get updated rubricVersionId
            if (workerId) {
                getProjectList();
            }
        };

        window.addEventListener(QA_RULE_UPDATED_EVENT, handleQaRuleUpdated);

        return () => {
            window.removeEventListener(QA_RULE_UPDATED_EVENT, handleQaRuleUpdated);
        };
    }, [workerId]);

    //for real-time count updates
    useEffect(() => {
        if (refreshableProjectsCount === 0) {
            return;
        }

        const interval = setInterval(() => {
            // Only refresh if user is on the page
            if (document.visibilityState === 'visible') {
                // Get fresh list on each poll - projectList is in closure scope, always current
                const currentProjects = [
                    ...projectList.active,
                    ...projectList.forReview,
                    ...projectList.archived.filter(project => project.availability !== false)
                ];
                if (currentProjects.length > 0) {
                    getProjectWorkItemCounts(currentProjects, true);
                }
            }
        }, REFRESH_INTERVAL_MS);

        return () => {
            clearInterval(interval);
        };
    }, [refreshableProjectsCount, REFRESH_INTERVAL_MS]);

    // Refresh counts when page becomes visible returning from work item detail
    useEffect(() => {
        if (refreshableProjectsCount === 0) {
            return;
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // Refresh counts when user returns to the page get fresh list from state
                const currentProjects = [
                    ...projectList.active,
                    ...projectList.forReview,
                    ...projectList.archived.filter(project => project.availability !== false)
                ];
                if (currentProjects.length > 0) {
                    getProjectWorkItemCounts(currentProjects, true);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [refreshableProjectsCount]);

    // Restore selected project from URL params after project list loads
    // Only runs when project list first loads or when explicitly needed (not on every count update)
    useEffect(() => {
        const hasProjects = projectList.active.length > 0 || projectList.forReview.length > 0 || projectList.archived.length > 0;
        
        // Don't auto-select if project is already selected - prevents overriding user's current selection
        if (!hasProjects || selectedProject) {
            return;
        }
        
        if (initialProjectId) {
            // Try to restore from URL params
            const allProjects = [...projectList.active, ...projectList.forReview, ...projectList.archived];
            const projectToSelect = allProjects.find(
                project => project.collection_id === initialProjectId
            );

            if (projectToSelect) {
                // Purposely check null as clicking on a project row shows all thus we shouldn't default to IN_CQA
                const statusToUse = selectedStatus === undefined
                    ? initialStatus
                    : selectedStatus;

                onProjectSelected(projectToSelect, statusToUse);
                return; // Exit early after restoring from URL
            }
        }
        
        // Only auto-select first project if no initialProjectId and no selectedProject
        // This should only happen on initial load when user hasn't selected anything yet
        if (!initialProjectId) {
            const firstProject = projectList.active.length > 0 
                ? projectList.active[0]
                : projectList.forReview.length > 0 
                    ? projectList.forReview[0]
                    : projectList.archived.length > 0 
                        ? projectList.archived[0]
                        : null;
            if (firstProject) {
                onProjectSelected(firstProject, selectedStatus);
            }
        }
    }, [projectList.active.length, projectList.forReview.length, projectList.archived.length, initialProjectId, initialStatus, selectedProject?.collection_id, selectedStatus, onProjectSelected]);

    // Watch for when counts are loaded and update the selected project if needed
    // This handles the case where a project is selected before counts are loaded
    useEffect(() => {
        if (selectedProject && onProjectUpdated && !countIsLoading) {
            // Find the project in the current project list which should have counts now
            const allProjects = [...projectList.active, ...projectList.forReview, ...projectList.archived];
            const projectWithCounts = allProjects.find(
                p => p.collection_id === selectedProject.collection_id && p.workItemCounts
            );
            
            // If we found the project with counts and the selected project doesn't have counts yet, update it
            if (projectWithCounts && (!selectedProject.workItemCounts || 
                JSON.stringify(selectedProject.workItemCounts) !== JSON.stringify(projectWithCounts.workItemCounts))) {
                onProjectUpdated(projectWithCounts);
            }
        }
    }, [projectList.active, projectList.forReview, projectList.archived, countIsLoading, selectedProject, onProjectUpdated]);

    const getProjectList = async () => {
        try {
            const response = await getQaRules(
                client, 
                "cqa_samples", 
                serviceLineMapper[userContext?.serviceLine?.name] ?? userContext?.serviceLine?.name, 
                []
            );

            setLoadingProjectList(false);

            const filteredProjects = response
                .filter(project => filterProjects(project, activeContextRoles, userRoles, workerId))
                .map(project => createProjectItemFromQaRule(project, activeContextRoles, workerId));

            // Initially separate by availability (before we have counts)
            const activeProjects = filteredProjects.filter(project => project.availability === true);
            const archivedProjects = filteredProjects.filter(project => project.availability === false);
            setProjectList({ active: activeProjects, forReview: [], archived: archivedProjects });

            if(activeProjects.length && !initialProjectId) {
                // When auto-selecting first project, preserve initialStatus from URL if available
                const statusToUse = initialStatus !== null && initialStatus !== undefined
                    ? initialStatus
                    : selectedStatus;
                onProjectSelected(activeProjects[0], statusToUse);
            } else if (activeProjects.length === 0) {
                // If no projects available, clear selection to prevent work items from loading
                onProjectSelected(null, selectedStatus);
            }

            // Fetch counts for all projects with availability is true
            // This will trigger categorization once counts are received
            if (activeProjects.length > 0) {
                getProjectWorkItemCounts(activeProjects);
            }

            // Fetch rubric info for all projects
            if (filteredProjects.length > 0) {
                fetchRubricInfo(filteredProjects);
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
            // Clear project list on error
            setProjectList({ active: [], forReview: [], archived: [] });
            // Clear selection to prevent work items from loading
            onProjectSelected(null, selectedStatus);
        }
    };

    // Helper function to categorize projects based on work item counts
    const categorizeProjects = (projects: CollectionWithWorkItemCounts[]) => {
        const active: CollectionWithWorkItemCounts[] = [];
        const forReview: CollectionWithWorkItemCounts[] = [];
        const archived: CollectionWithWorkItemCounts[] = [];

        projects.forEach(project => {
            // If availability is explicitly false, always archive regardless of counts (manual toggle)
            if (project.availability === false) {
                archived.push(project);
                return;
            }

            const toDoCount = project.workItemCounts?.[WorkitemStatus.IN_CQA] || 0;
            const completeCount = project.workItemCounts?.[WorkitemStatus.CQA_COMPLETE] || 0;

            if (toDoCount > 0) {
                // Has items To Do - goes to Active
                active.push(project);
            } else if (toDoCount === 0 && completeCount > 0) {
                // To Do is 0 but has Complete items - goes to For Review
                forReview.push(project);
            } else if (toDoCount === 0 && completeCount === 0) {
                // To Do is 0 and Complete is 0 - goes to Archived (auto-archived)
                archived.push(project);
            } else {
                // Fallback: if counts are undefined or null, assume active
                active.push(project);
            }
        });

        return { active, forReview, archived };
    };

    const getProjectWorkItemCounts = async (collections: CollectionWithWorkItemCounts[], isPolling = false) => {
        if (!isPolling) {
            setCountIsLoading(true);
            if (onCountIsLoadingChange) {
                onCountIsLoadingChange(true);
            }
        }

        const collectionIds = collections.map(collection => collection.collection_id);
        if (collectionIds.length === 0) {
            if (!isPolling) {
                setCountIsLoading(false);
            }
            return;
        }

        try {
            // Update projects with counts
            const projectsWithCounts = await fetchProjectsWithCounts(collectionIds, collections, activeContextRoles, workerId, client, isPolling);

            // Merge with existing projects and update with new counts
            setProjectList(prev => {
                // Combine all existing projects with newly fetched ones
                const allProjectsMap = new Map<string, CollectionWithWorkItemCounts>();
                
                // Add all existing projects
                [...prev.active, ...prev.forReview, ...prev.archived].forEach(p => {
                    allProjectsMap.set(p.collection_id, p);
                });
                
                // Update with new counts (this will overwrite existing projects with same ID)
                projectsWithCounts.forEach(p => {
                    allProjectsMap.set(p.collection_id, p);
                });

                // Get all projects with updated counts
                const allUpdatedProjects = Array.from(allProjectsMap.values());
                
                // Re-categorize all projects based on latest counts
                // (categorizeProjects already handles availability=false projects for manual toggle)
                const finalCategorized = categorizeProjects(allUpdatedProjects);

                return finalCategorized;
            });

            // Only update selectedProject during initial fetch, not during polling.
            // During polling, the useEffect that watches projectList changes will handle updates.
            // This avoids stale closure issues where the polling interval captures an old
            // selectedProject reference and incorrectly resets selection to a previous project.
            if (!isPolling) {
                const currentSelectedProject = selectedProject;
                if (currentSelectedProject && onProjectUpdated) {
                    const updatedSelectedProject = projectsWithCounts.find(
                        p => p.collection_id === currentSelectedProject.collection_id
                    );
                    if (updatedSelectedProject) {
                        onProjectUpdated(updatedSelectedProject);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching project work item counts:", error);
        } finally {
            if (!isPolling) {
                setCountIsLoading(false);
                if (onCountIsLoadingChange) {
                    onCountIsLoadingChange(false);
                }
            }
        }
    };

    // Fetch rubric info for all projects with rubricVersionIds
    // Uses ref to avoid dependency on rubricInfoCache state which would cause unnecessary re-renders
    const fetchRubricInfo = useCallback(async (projects: CollectionWithWorkItemCounts[]) => {
        const rubricVersionIds = projects
            .map(p => p.rubricVersionId)
            .filter((id): id is number => id !== undefined && id !== null);

        const uniqueIds = [...new Set(rubricVersionIds)];

        // Filter out IDs we already have cached (using ref for current value)
        const idsToFetch = uniqueIds.filter(id => !rubricInfoCacheRef.current.has(id));

        if (idsToFetch.length === 0) {
            return;
        }

        // Fetch rubric info for each unique version ID
        await Promise.all(idsToFetch.map(async (versionId) => {
            try {
                const fullVersion = await fetchFullRubricVersion(versionId);
                rubricInfoCacheRef.current.set(versionId, {
                    name: fullVersion?.rubric?.name ?? 'Unknown',
                    version: `${fullVersion?.version?.majorVersion ?? '0'}.${fullVersion?.version?.minorVersion ?? '0'}`
                });
            } catch (error) {
                console.error(`Failed to fetch rubric version ${versionId}:`, error);
            }
        }));

        // Trigger re-render with the updated cache
        setRubricInfoCache(new Map(rubricInfoCacheRef.current));
    }, []);

    // Filter projects by search term
    const filterBySearch = useCallback((projects: CollectionWithWorkItemCounts[]) => {
        if (!searchTerm) return projects;
        return projects.filter(p =>
            p.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm]);

    const filteredActive = filterBySearch(projectList.active);
    const filteredForReview = filterBySearch(projectList.forReview);
    const filteredArchived = filterBySearch(projectList.archived);

    // Helper: Create project item with nested status items
    /* When clicking a project we purposely set status to null for url param tracking. When we don't have a status we show all workitems.
     * When expanding a project we default to IN_CQA */
    const createProjectItem = (project: CollectionWithWorkItemCounts) => {
        const totalWorkItemCounts: number = project.workItemCounts
            ? (Object.values(omitKey(project.workItemCounts, WorkitemStatus.ASSIGNED_TO_ME)) as number[]).reduce((acc: number, count: number) => acc + count, 0)
            : 0;

        const isSelected = selectedProject?.collection_id === project.collection_id;

        // Build rubric subtitle if rubric info is available (use ref for current value)
        const rubricInfo = project.rubricVersionId
            ? rubricInfoCacheRef.current.get(project.rubricVersionId)
            : null;
        const subtitle = rubricInfo
            ? `${rubricInfo.name} v${rubricInfo.version}`
            : undefined;

        return (
            <Box key={project.collection_id} sx={{ position: 'relative' }}>
                <ListItem
                    title={project.name}
                    subtitle={subtitle}
                    badge={showCounts && !countIsLoading ? totalWorkItemCounts : undefined}
                    badgeLoading={countIsLoading && showCounts}
                    onClick={() => {
                        // Pass undefined to default to first status (Remaining Cases/To do)
                        onProjectSelected(project, undefined);
                    }}
                    selected={isSelected}
                    textColor={NAVIGATION_COLORS.TEXT_PRIMARY}
                    selectedTextColor={NAVIGATION_COLORS.TEXT_SELECTED}
                    selectedBackgroundColor={NAVIGATION_COLORS.SELECTED_BG}
                    hoverBackgroundColor={NAVIGATION_COLORS.HOVER_BG}
                    badgeTextColor={theme.palette.artisticTextLow.main}
                    sx={{
                        padding: '12px 16px 12px 12px',
                        paddingRight: isSelected ? '28px' : '14px', 
                        borderRadius: '3px',
                        margin: '0 12px 4px 12px',
                        '& .MuiListItemText-primary': {
                            fontWeight: isSelected ? 600 : 400,
                            fontSize: '14px', 
                            lineHeight: '16px',
                        },
                        // Add margin to badge to create space between badge and column edge 
                        '& > .MuiBox-root:not(:first-of-type)': {
                            marginRight: isSelected ? '28px' : '12px' 
                        }
                    }}
                />
                {isSelected && (
                    <ChevronRightIcon
                        sx={{
                            position: 'absolute',
                            right: '16px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            fontSize: 14,
                            color: NAVIGATION_COLORS.TEXT_LOW,
                            pointerEvents: 'none',
                            zIndex: 1
                        }}
                    />
                )}
            </Box>
        );
    };

    // Build sections configuration
    const sections: ListSection[] = [
        // Active Projects Section
        {
            hideHeader: true,
            rows: [
                <ListItem
                    key="active-header"
                    title={`Active Projects (Review) ${filteredActive ? `(${filteredActive.length})` : ''}`}
                    icon={
                        <img
                            src={isActiveExpanded ? MinusBoxIcon : AddBoxIcon}
                            alt={isActiveExpanded ? "collapse" : "expand"}
                            style={{ width: '20px', height: '20px' }}
                        />
                    }
                    expanded={isActiveExpanded}
                    onClick={() => {
                        const newValue = !isActiveExpanded;
                        setIsActiveExpanded(newValue);
                        if (newValue) {
                            setIsForReviewExpanded(false);
                            setIsArchivedExpanded(false);
                            sessionStorage.setItem(STORAGE_KEYS.QA_EXPANDED_SECTION, 'active');
                        } else {
                            sessionStorage.removeItem(STORAGE_KEYS.QA_EXPANDED_SECTION);
                        }
                    }}
                    textColor={NAVIGATION_COLORS.TEXT_PRIMARY}
                >
                    {isActiveExpanded && filteredActive?.map(createProjectItem)}
                </ListItem>
            ],
            showDivider: true
        },
        // For Review Projects Section
        {
            hideHeader: true,
            rows: [
                <ListItem
                    key="for-review-header"
                    title={`Projects For Review ${filteredForReview ? `(${filteredForReview.length})` : ''}`}
                    icon={
                        <img
                            src={isForReviewExpanded ? MinusBoxIcon : AddBoxIcon}
                            alt={isForReviewExpanded ? "collapse" : "expand"}
                            style={{ width: '20px', height: '20px' }}
                        />
                    }
                    expanded={isForReviewExpanded}
                    onClick={() => {
                        const newValue = !isForReviewExpanded;
                        setIsForReviewExpanded(newValue);
                        if (newValue) {
                            setIsActiveExpanded(false);
                            setIsArchivedExpanded(false);
                            sessionStorage.setItem(STORAGE_KEYS.QA_EXPANDED_SECTION, 'forReview');
                        } else {
                            sessionStorage.removeItem(STORAGE_KEYS.QA_EXPANDED_SECTION);
                        }
                    }}
                    textColor={NAVIGATION_COLORS.TEXT_PRIMARY}
                >
                    {isForReviewExpanded && filteredForReview?.map(createProjectItem)}
                </ListItem>
            ],
            showDivider: true
        },
        // Archived Projects Section
        {
            hideHeader: true,
            rows: [
                <ListItem
                    key="archived-header"
                    title={`Archived Projects ${filteredArchived ? `(${filteredArchived.length})` : ''}`}
                    icon={
                        <img
                            src={isArchivedExpanded ? MinusBoxIcon : AddBoxIcon}
                            alt={isArchivedExpanded ? "collapse" : "expand"}
                            style={{ width: '20px', height: '20px' }}
                        />
                    }
                    expanded={isArchivedExpanded}
                    onClick={() => {
                        const newValue = !isArchivedExpanded;
                        setIsArchivedExpanded(newValue);
                        if (newValue) {
                            setIsActiveExpanded(false);
                            setIsForReviewExpanded(false);
                            sessionStorage.setItem(STORAGE_KEYS.QA_EXPANDED_SECTION, 'archived');
                        } else {
                            sessionStorage.removeItem(STORAGE_KEYS.QA_EXPANDED_SECTION);
                        }
                    }}
                    textColor={NAVIGATION_COLORS.TEXT_PRIMARY}
                >
                    {isArchivedExpanded && filteredArchived?.map(createProjectItem)}
                </ListItem>
            ]
        }
    ];

    return (
        <ConfigurableList
            sections={sections}
            containerStyle={{
                backgroundColor: 'transparent',
                margin: 0,
                padding: 0,
            }}
            textColor={NAVIGATION_COLORS.TEXT_PRIMARY}
        />
    );
};

// Export the event so other components can dispatch it
export { WORK_ITEMS_UPDATED_EVENT };

export default QANavigationContent;
